/**
 * 18+ age-gate module.
 *
 * On first visit, the overlay covers the page. Once confirmed, the choice
 * is persisted:
 *   - localStorage if the user ticks "Remember me" (or by default on
 *     subsequent visits, see `defaultRemember`).
 *   - sessionStorage as a fallback when localStorage is unavailable
 *     (private browsing, file://, etc.).
 *
 * The body is locked from scrolling while the gate is open. A focus trap
 * keeps keyboard navigation inside the dialog.
 *
 * Public API:
 *   initAgeGate({ storage, defaultRemember } = {}) -> () => void
 *     The returned function re-opens the gate (used by the footer link).
 */

const STORAGE_KEY = 'sg_age_verified';
const SESSION_KEY = 'sg_age_verified_session';
const REMEMBER_KEY = 'sg_age_remember';

function getStorage(preferLocal) {
  try {
    const s = preferLocal ? window.localStorage : window.sessionStorage;
    const probe = '__sg_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch (_e) {
    return null;
  }
}

function isVerified(remember) {
  const local = getStorage(true);
  if (local && local.getItem(STORAGE_KEY) === 'true' && local.getItem(REMEMBER_KEY) === 'true') {
    return true;
  }
  const session = getStorage(false);
  if (session && session.getItem(SESSION_KEY) === 'true') {
    return true;
  }
  // If they asked to be remembered but localStorage is unavailable, fall
  // back to a session verification so the gate doesn't re-open mid-read.
  return !remember && session !== null && session.getItem(SESSION_KEY) === 'true';
}

function persistVerification(remember) {
  const local = getStorage(true);
  if (local) {
    if (remember) {
      local.setItem(STORAGE_KEY, 'true');
      local.setItem(REMEMBER_KEY, 'true');
    } else {
      local.removeItem(STORAGE_KEY);
      local.removeItem(REMEMBER_KEY);
    }
  }
  const session = getStorage(false);
  if (session) {
    session.setItem(SESSION_KEY, 'true');
  }
}

function clearVerification() {
  const local = getStorage(true);
  if (local) {
    local.removeItem(STORAGE_KEY);
    local.removeItem(REMEMBER_KEY);
  }
  const session = getStorage(false);
  if (session) {
    session.removeItem(SESSION_KEY);
  }
}

function trapFocus(container) {
  const focusables = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  if (!focusables.length) return () => {};
  const first = focusables[0];
  const last = focusables[focusables.length - 1];

  function handler(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  container.addEventListener('keydown', handler);
  return () => container.removeEventListener('keydown', handler);
}

function open(gate, dialog) {
  gate.hidden = false;
  document.body.classList.add('is-locked');
  const release = trapFocus(dialog);
  const confirm = dialog.querySelector('[data-age-confirm]');
  if (confirm) confirm.focus();
  // Stash release fn so close() can call it.
  gate.dataset._release = '1';
  dialog._releaseFocus = release;
}

function close(gate, dialog) {
  gate.hidden = true;
  document.body.classList.remove('is-locked');
  if (dialog._releaseFocus) {
    dialog._releaseFocus();
    dialog._releaseFocus = null;
  }
}

export function initAgeGate(options = {}) {
  const { defaultRemember = true } = options;
  const gate = document.getElementById('age-gate');
  if (!gate) return () => {};

  const dialog = gate.querySelector('.age-gate_card') || gate;
  const confirmBtn = gate.querySelector('[data-age-confirm]');
  const declineBtn = gate.querySelector('[data-age-decline]');
  const rememberCheckbox = gate.querySelector('[data-age-remember]');

  if (rememberCheckbox) {
    rememberCheckbox.checked = defaultRemember;
  }

  if (isVerified(defaultRemember)) {
    close(gate, dialog);
  } else {
    open(gate, dialog);
  }

  function onConfirm() {
    const remember = rememberCheckbox ? rememberCheckbox.checked : defaultRemember;
    persistVerification(remember);
    close(gate, dialog);
  }

  function onDecline() {
    // Send under-18 visitors somewhere safe.
    window.location.href = 'https://www.google.com';
  }

  if (confirmBtn) confirmBtn.addEventListener('click', onConfirm);
  if (declineBtn) declineBtn.addEventListener('click', onDecline);

  // Expose a reset hook (useful for tests, and a footer "click here if
  // you're not 18+" link can call it too).
  gate._reset = () => {
    clearVerification();
    open(gate, dialog);
  };

  return () => {
    if (gate._reset) gate._reset();
  };
}
